import { LoadingParams } from 'framework/list';
import { useBusinessEngineApi, useKernelApi, usePeopleManagementApi } from 'infrastructure/api';
import { useCrudService } from 'framework/crud';
import { cloneDeep } from 'lodash';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { useProjectService } from 'domain/shared/project';
import { DashboardEmployee } from './types';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { DELEGATED_EMPLOYEE_ZONES_ALIASES } from './constants';
import { EMPLOYEE_ZONES_ALIASES, followUpValidation } from 'domain/shared/employee';

const RESOURCE_URL = '/employees/dashboard';

const healthcheckValidation = {
  date: {
    type: 'string',
    required: true,
  },
  description: {
    type: 'string',
    required: false,
  },
};

const normalizeDashboardSearchParams = (
  loadParams: LoadingParams,
  options: { stripRiskStatusFilter?: boolean } = {},
) => {
  const searchParams = cloneDeep(loadParams);
  const searchString = searchParams.filter?.['specification:search']?.eq || '';

  if (searchString && searchParams.filter) {
    delete searchParams.filter?.['specification:search'];
    searchParams.filter.searchString = {
      ct: searchString,
    };
  }

  const riskStatusFilter = searchParams.filter?.['specification:employeeByRetentionRiskStatusSpecification'];

  if (options.stripRiskStatusFilter && riskStatusFilter && searchParams.filter) {
    delete searchParams.filter?.['specification:employeeByRetentionRiskStatusSpecification'];
  }

  return searchParams;
};

const mapDashboardEmployees = (employees: DashboardEmployee[]) => {
  return employees.map((item: DashboardEmployee) => ({
    ...item,
    retentionRiskFollowUps: item.retentionRiskFollowUps.sort(
      (a, b) => new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime(),
    ),
    id: item?.retentionRiskId, // === retentionRiskId (Need for List)
  })) as DashboardEmployee[];
};

const getRiskStatusFilterValues = (loadParams: LoadingParams) => {
  return loadParams.filter?.['specification:employeeByRetentionRiskStatusSpecification']?.in || [];
};

type RetentionRiskIssue = NonNullable<DashboardEmployee['retentionRiskIssues']>[number];

const useEmployeesDashboardService = () => {
  const { addMessage } = useMessageService();
  const { sendGetRequest } = useBusinessEngineApi();
  const { sendPostRequest, sendPatchRequest, sendDeleteRequest, sendPutRequest } = usePeopleManagementApi();
  const { sendPostRequest: sendKernalPostRequest } = useKernelApi();
  const { searchAll: searchAllProjects } = useProjectService();
  const { searchAll } = useCrudService({
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    listResourceUrl: RESOURCE_URL,
    useService: usePeopleManagementApi,
  });

  const getProjectNames = (projectIds: string[] | undefined, projectNamesMap: Record<string, string>) => {
    return (projectIds || []).reduce<string[]>((acc, projectId) => {
      const projectName = projectNamesMap[projectId];

      if (projectName) acc.push(projectName);

      return acc;
    }, []);
  };

  const appendProjectNamesToIssueDescription = (
    issue: RetentionRiskIssue,
    projectNamesMap: Record<string, string>,
  ): RetentionRiskIssue => {
    const projectNames = getProjectNames(issue.projectIds, projectNamesMap);

    if (!projectNames.length) return issue;

    return {
      ...issue,
      description: `${issue.description} (${projectNames.join(', ')})`,
    };
  };

  const getEmployeesProjectIds = (employees: DashboardEmployee[]) =>
    employees.reduce<string[]>(
      (acc, employee) =>
        employee.retentionRiskIssues.reduce<string[]>(
          (issueAcc, issue) => (issue.projectIds?.length ? issueAcc.concat(issue.projectIds) : issueAcc),
          acc,
        ),
      [],
    );

  const appendProjectNamesToEmployeesIssuesDescription = async (
    employees: DashboardEmployee[],
  ): Promise<DashboardEmployee[]> => {
    const projectIds = getEmployeesProjectIds(employees);

    if (!projectIds.length) return employees;

    const projects = await searchAllProjects({
      filter: {
        id: {
          in: projectIds,
        },
      },
    });

    const projectNamesMap = projects.reduce<Record<string, string>>((acc, project) => {
      if (project?.id && project?.name) acc[String(project.id)] = project.name;

      return acc;
    }, {});

    return employees.map(employee => ({
      ...employee,
      retentionRiskIssues: employee.retentionRiskIssues.map(issue =>
        appendProjectNamesToIssueDescription(issue, projectNamesMap),
      ),
    }));
  };

  const appendProjectNamesToIssuesDescription = async (issues: RetentionRiskIssue[]): Promise<RetentionRiskIssue[]> => {
    const projectIds = issues.flatMap(issue => issue.projectIds || []);

    if (!projectIds.length) return issues;

    const projects = await searchAllProjects({
      filter: {
        id: {
          in: projectIds,
        },
      },
    });

    const projectNamesMap = projects.reduce<Record<string, string>>((acc, project) => {
      if (project?.id && project?.name) acc[String(project.id)] = project.name;

      return acc;
    }, {});

    return issues.map(issue => appendProjectNamesToIssueDescription(issue, projectNamesMap));
  };

  const searchAllDelegatedEntries = async (params = {}) => {
    let totalPages = 0;
    let currentPage = 0;
    let result: DashboardEmployee[] = [];

    do {
      // eslint-disable-next-line no-await-in-loop
      const request = await sendPostRequest('/employees/delegated/dashboard/search', {
        ...params,
        pageNumber: currentPage,
      });
      // eslint-disable-next-line no-await-in-loop

      const data = await request.json();

      currentPage += 1;
      totalPages = data.totalPages;
      result = [...result, ...(data.result || [])];
    } while (currentPage < totalPages);

    return result as DashboardEmployee[];
  };

  const getSharedZonesAccordingToFilter = (loadParams: LoadingParams) => {
    const riskStatusFilterValues = getRiskStatusFilterValues(loadParams);
    const filteredSharedZones = riskStatusFilterValues.filter(
      (zone: string) => zone !== DELEGATED_EMPLOYEE_ZONES_ALIASES.DELEGATED,
    ) as Array<(typeof EMPLOYEE_ZONES_ALIASES)[keyof typeof EMPLOYEE_ZONES_ALIASES]>;

    if (filteredSharedZones.length > 0) {
      return filteredSharedZones;
    }

    if (
      riskStatusFilterValues.length === 1 &&
      riskStatusFilterValues.includes(DELEGATED_EMPLOYEE_ZONES_ALIASES.DELEGATED)
    ) {
      return [];
    }

    return Object.values(EMPLOYEE_ZONES_ALIASES);
  };

  const buildLoadParamsWithZoneFilter = (
    loadParams: LoadingParams,
    zone: (typeof EMPLOYEE_ZONES_ALIASES)[keyof typeof EMPLOYEE_ZONES_ALIASES],
  ): LoadingParams => {
    const zoneLoadParams = cloneDeep(loadParams);

    zoneLoadParams.filter = {
      ...zoneLoadParams.filter,
      'specification:employeeByRetentionRiskStatusSpecification': {
        in: [zone],
      },
    };

    return zoneLoadParams;
  };

  const loadEmployeesForDashboard = async (loadParams: LoadingParams): Promise<DashboardEmployee[]> => {
    const searchParams = normalizeDashboardSearchParams(loadParams);

    return await searchAll({
      ...(searchParams as any),
      filter: {
        ...searchParams.filter,
      },
    });
  };

  const fetchAllEmployeesForDashboard = async (loadParams: LoadingParams) => {
    const res = await loadEmployeesForDashboard(loadParams);
    const employeesWithProjectNames = await appendProjectNamesToEmployeesIssuesDescription(res);

    return {
      result: mapDashboardEmployees(employeesWithProjectNames),
      totalPages: 1,
      totalElements: employeesWithProjectNames.length,
    };
  };

  const getAllEmployeesForDashboardParallelByZones = async (loadParams: LoadingParams) => {
    const sharedZones = getSharedZonesAccordingToFilter(loadParams);

    if (sharedZones.length === 0) {
      return {
        result: [],
        totalPages: 1,
        totalElements: 0,
      };
    }

    const zoneResults = await Promise.allSettled(
      sharedZones.map(async zone => ({
        zone,
        response: await loadEmployeesForDashboard(buildLoadParamsWithZoneFilter(loadParams, zone)),
      })),
    );

    const fulfilledResults = zoneResults.filter(
      (
        result,
      ): result is PromiseFulfilledResult<{
        zone: (typeof EMPLOYEE_ZONES_ALIASES)[keyof typeof EMPLOYEE_ZONES_ALIASES];
        response: DashboardEmployee[];
      }> => result.status === 'fulfilled',
    );

    const rejectedResults = zoneResults.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );

    if (rejectedResults.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('[EmployeesDashboard] parallel zone fetch failed', {
        zones: sharedZones,
        rejectedCount: rejectedResults.length,
        errors: rejectedResults.map(result => result.reason),
      });
    }

    if (fulfilledResults.length === 0) {
      addMessage(new ErrorMessage('Error fetching employess list.'));

      return {
        result: [],
        totalPages: 1,
        totalElements: 0,
      };
    }

    const employees = fulfilledResults.flatMap(zoneResult => zoneResult.value.response);
    const employeesWithProjectNames = await appendProjectNamesToEmployeesIssuesDescription(employees);

    return {
      result: mapDashboardEmployees(employeesWithProjectNames),
      totalPages: 1,
      totalElements: employeesWithProjectNames.length,
    };
  };

  const getCombinedEmployeesForDashboard = async (loadParams: LoadingParams) => {
    const riskStatusFilterValues =
      loadParams.filter?.['specification:employeeByRetentionRiskStatusSpecification']?.in || [];

    const fetchDelegatedEmployeesAccordingToFilter = async (filterZones: string[]) => {
      if (
        loadParams.filter &&
        filterZones &&
        filterZones.length > 0 &&
        !filterZones.includes(DELEGATED_EMPLOYEE_ZONES_ALIASES.DELEGATED)
      ) {
        return {
          result: [],
          totalPages: 1,
          totalElements: 0,
        };
      }

      return await getDelegatedEmployeesForDashboard(loadParams);
    };

    const fetchSharedEmployeesAccordingToFilter = async (filterZones: string[]) => {
      if (
        loadParams.filter &&
        filterZones &&
        filterZones.length === 1 &&
        filterZones.includes(DELEGATED_EMPLOYEE_ZONES_ALIASES.DELEGATED)
      ) {
        return {
          result: [],
          totalPages: 1,
          totalElements: 0,
        };
      }

      return await getAllEmployessForDashboardParallelByZones(loadParams);
    };

    const allEmployees = await fetchSharedEmployeesAccordingToFilter(riskStatusFilterValues);
    const delegatedEmployees = await fetchDelegatedEmployeesAccordingToFilter(riskStatusFilterValues);

    return {
      result: [...allEmployees.result, ...delegatedEmployees.result],
      totalPages: 1,
      totalElements: allEmployees.totalElements + delegatedEmployees.totalElements,
    };
  };

  const getDelegatedEmployeesForDashboard = async (loadParams: LoadingParams) => {
    try {
      const searchParams = normalizeDashboardSearchParams(loadParams, { stripRiskStatusFilter: true });

      const res = await searchAllDelegatedEntries({
        ...(searchParams as any),
        filter: {
          ...searchParams.filter,
        },
      });
      const employeesWithProjectNames = await appendProjectNamesToEmployeesIssuesDescription(res);
      const resultWithActiveIssues = employeesWithProjectNames.filter(
        dashboardRisk => dashboardRisk.retentionRiskIssues.length > 0,
      );

      return {
        result: mapDashboardEmployees(resultWithActiveIssues),
        totalPages: 1,
        totalElements: employeesWithProjectNames.length,
      };
    } catch {
      addMessage(new ErrorMessage('Error fetching delegated employees list.'));

      return {
        result: [],
        totalPages: 1,
        totalElements: 0,
      };
    }
  };

  const getAllEmployessForDashboard = async (loadParams: LoadingParams) => {
    try {
      return await fetchAllEmployeesForDashboard(loadParams);
    } catch {
      addMessage(new ErrorMessage('Error fetching employess list.'));
      return {
        result: [],
        totalPages: 1,
        totalElements: 0,
      };
    }
  };

  const getAllEmployessForDashboardParallelByZones = async (loadParams: LoadingParams) => {
    try {
      return await getAllEmployeesForDashboardParallelByZones(loadParams);
    } catch {
      addMessage(new ErrorMessage('Error fetching employess list.'));
      return {
        result: [],
        totalPages: 1,
        totalElements: 0,
      };
    }
  };

  const getEmployeeProcesses = async (alias: string) => {
    try {
      const legacyUrl = `/camunda/processes/groups/EMPLOYEE/${alias}`;
      const v2Url = `/camunda/processes/groups/v2/EMPLOYEE/${alias}`;

      const fetchProcesses = (url: string) => sendGetRequest(url).then(response => response.json());

      const results = await Promise.allSettled([fetchProcesses(legacyUrl), fetchProcesses(v2Url)]);

      const fulfilledData = results.flatMap(result =>
        result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : [],
      );

      if (results.every(result => result.status === 'rejected')) {
        const firstRejected = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
        throw firstRejected?.reason ?? new Error('Failed to fetch employee processes');
      }

      return fulfilledData;
    } catch (error) {
      addMessage(new ErrorMessage('Error fetching employee processes.'));
      return [];
    }
  };

  const assigneEmployeeAction = async (riskId: string, data: FormFieldsData) => {
    const request = await sendPatchRequest(`/retention-risks/${riskId}/assign`, data);

    return request.json();
  };

  const createFollowUp = async (riskId: string, data: FormFieldsData) => {
    const request = await sendPostRequest(`/retention-risks/${riskId}/follow-up`, data);

    return request.json();
  };

  const updateFollowUp = async (riskId: string, followUpId: string, data: FormFieldsData) => {
    const request = await sendPutRequest(`/retention-risks/${riskId}/follow-up/${followUpId}`, data);

    return request.json();
  };

  const deleteFollowUp = async (riskId: string, followUpId: string) => {
    return sendDeleteRequest(`/retention-risks/${riskId}/follow-up/${followUpId}`);
  };

  const followUpValidationShema = followUpValidation();
  const getFollowUpValidation = () => Promise.resolve(followUpValidationShema);

  const updateHealthcheck = async (riskId: string, employeeAlias: string, data: FormFieldsData) => {
    const request = await sendPatchRequest(`/retention-risks/${riskId}/healthcheck`, { date: data.date });

    await sendKernalPostRequest(`/employees/${employeeAlias}/notes`, {
      attachmentsIds: [],
      noteText: `<p><strong>Healthcheck</strong><br><br><strong>Date</strong>: ${data.date}<br><strong>Description</strong>: ${data.description}</p>`,
      employeeId: employeeAlias,
    });

    return request.json();
  };

  const changeZonePriority = async (riskId: string, data: FormFieldsData) => {
    const request = await sendPatchRequest(`/retention-risks/${riskId}/priority`, data);

    return request.json();
  };

  const updateIssues = async (riskId: string, data: FormFieldsData) => {
    const request = await sendPostRequest(`/retention-risks/${riskId}/issues/submit`, data);

    return request.json();
  };

  const getValidationHelthchekSchema = () => Promise.resolve(healthcheckValidation);

  return {
    getCombinedEmployeesForDashboard,
    getAllEmployessForDashboard,
    getAllEmployessForDashboardParallelByZones,
    createFollowUp,
    updateFollowUp,
    deleteFollowUp,
    getFollowUpValidation,
    assigneEmployeeAction,
    updateHealthcheck,
    changeZonePriority,
    updateIssues,
    appendProjectNamesToIssuesDescription,
    getEmployeeProcesses,
    getValidationHelthchekSchema,
  };
};

export default useEmployeesDashboardService;
